/*
 * Glamora: collection + search filtering and sorting.
 * Progressive enhancement over plain GET forms: without JS the Apply button and the
 * sort form still work. With JS, filter changes auto-apply (debounced) and results are
 * swapped in place through the Section Rendering API, keeping the drawer open.
 */
(() => {
  if (!('customElements' in window) || customElements.get('facets-section')) return;

  const DEBOUNCE_MS = 450;
  const CACHE_LIMIT = 20;

  const debounce = (fn, wait) => {
    let timer;
    const debounced = (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
  };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isPlainLeftClick = (event) =>
    !(event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);

  const relativeUrl = (value) => {
    const url = new URL(value, window.location.origin);
    return url.pathname + url.search;
  };

  class FacetsSection extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.controller = null;
      this.lastToggle = null;
      this.drawerWasOpen = false;
      this.stickyHeaders = [];
      this.stickyTop = null;
      this.ticking = false;

      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.queueFilters = debounce(() => this.applyFilters(false), DEBOUNCE_MS);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.currentUrl = window.location.pathname + window.location.search;
      this.drawer = this.querySelector('[data-facets-drawer]');
      this.toolbar = this.querySelector('[data-facets-toolbar]');

      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);

      if (this.drawer) {
        this.drawerWasOpen = this.drawer.classList.contains('is-open');
        this.setDrawerInert(!this.drawerWasOpen);
        this.observer = new MutationObserver(() => this.syncDrawerState());
        this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      }

      if (this.toolbar) {
        this.findStickyHeaders();
        this.updateStickyTop();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.queueFilters.cancel();
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
      // The theme editor re-renders sections: never leave the page locked behind an orphaned drawer.
      if (this.drawer && this.drawer.classList.contains('is-open')) this.closeDrawer();
    }

    /* ---------- Events ---------- */

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-sort-select]')) {
        this.applySort(target.value);
        return;
      }
      if (target.closest('[data-facets-form]')) {
        // A newer choice wins: drop any request that would overwrite it with stale state.
        if (this.controller) this.controller.abort();
        this.queueFilters();
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (form.matches('[data-facets-form]')) {
        event.preventDefault();
        this.queueFilters.cancel();
        this.applyFilters(true);
      } else if (form.matches('[data-sort-form]')) {
        event.preventDefault();
        const select = form.querySelector('[data-sort-select]');
        if (select) this.applySort(select.value);
      }
    }

    onClick(event) {
      const opener = event.target.closest('[data-facets-open]');
      if (opener) {
        event.preventDefault();
        this.openDrawer(opener);
        return;
      }

      const closer = event.target.closest('[data-facets-close]');
      if (closer) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const link = event.target.closest('a[data-facets-link]');
      if (link && isPlainLeftClick(event)) {
        event.preventDefault();
        this.queueFilters.cancel();
        this.render(relativeUrl(link.href));
      }
    }

    onKeydown(event) {
      // global.js closes drawers on Escape; this is only the fallback when it is not available.
      if (event.key !== 'Escape' || !this.drawer || !this.drawer.classList.contains('is-open')) return;
      if (window.Glamora && typeof window.Glamora.closeDrawer === 'function') return;
      this.closeDrawer();
    }

    onPopState() {
      const url = window.location.pathname + window.location.search;
      if (url === this.currentUrl) return;
      this.render(url, false);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.ticking = false;
        this.updateStickyTop();
      });
    }

    onResize() {
      this.findStickyHeaders();
      this.onScroll();
    }

    /* ---------- Filtering + sorting ---------- */

    applyFilters(closeDrawer) {
      const form = this.querySelector('[data-facets-form]');
      if (!form) return;
      const url = this.urlFromForm(form);
      if (closeDrawer) this.closeDrawer();
      if (url === this.currentUrl) return;
      this.render(url);
    }

    applySort(value) {
      const url = new URL(this.currentUrl, window.location.origin);
      if (value) {
        url.searchParams.set('sort_by', value);
      } else {
        url.searchParams.delete('sort_by');
      }
      url.searchParams.delete('page');
      this.querySelectorAll('[data-facets-sort-input]').forEach((input) => {
        input.value = value;
      });
      this.render(url.pathname + url.search);
    }

    urlFromForm(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (typeof value !== 'string') return;
        const trimmed = value.trim();
        if (trimmed !== '') params.append(key, trimmed);
      });
      const action = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
      const query = params.toString();
      return action.pathname + (query ? `?${query}` : '');
    }

    async render(url, pushState = true) {
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.setLoading(true);

      try {
        const html = await this.fetchSectionHtml(url, controller.signal);
        if (controller.signal.aborted) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (!this.update(doc)) throw new Error('Facets: results markup missing');
        this.currentUrl = url;
        if (pushState) window.history.pushState({ facets: this.sectionId }, '', url);
        this.scrollToResults();
        document.dispatchEvent(new CustomEvent('glamora:facets-updated', { detail: { url, sectionId: this.sectionId } }));
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        // Anything unexpected: fall back to a normal page load of the same URL.
        window.location.assign(url);
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.setLoading(false);
        }
      }
    }

    async fetchSectionHtml(url, signal) {
      if (this.cache.has(url)) return this.cache.get(url);
      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);
      const response = await fetch(requestUrl.pathname + requestUrl.search, {
        signal,
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Facets: HTTP ${response.status}`);
      const html = await response.text();
      this.cache.set(url, html);
      if (this.cache.size > CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value);
      return html;
    }

    update(doc) {
      const results = this.querySelector('[data-facets-results]');
      const freshResults = doc.querySelector('[data-facets-results]');
      if (!results || !freshResults) return false;
      results.innerHTML = freshResults.innerHTML;

      this.swap(doc, '[data-facets-active]');
      this.swap(doc, '[data-facets-badge]');
      this.swap(doc, '[data-sort-hidden]');

      const freshCount = doc.querySelector('[data-facets-count]');
      if (freshCount) {
        const text = freshCount.textContent.trim();
        this.querySelectorAll('[data-facets-count]').forEach((el) => {
          if (el.textContent.trim() !== text) el.textContent = text;
        });
      }

      const freshSelect = doc.querySelector('[data-sort-select]');
      if (freshSelect) {
        const selected = freshSelect.querySelector('option[selected]') || freshSelect.querySelector('option');
        if (selected) {
          this.querySelectorAll('[data-sort-select]').forEach((select) => {
            if (select.value !== selected.value) select.value = selected.value;
          });
        }
      }

      this.updateForm(doc);
      return true;
    }

    swap(doc, selector) {
      const fresh = doc.querySelector(selector);
      const current = this.querySelector(selector);
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }

    updateForm(doc) {
      const form = this.querySelector('[data-facets-form]');
      const fresh = doc.querySelector('[data-facets-form]');
      if (!form || !fresh) return;

      const openState = new Map();
      form.querySelectorAll('details[id]').forEach((details) => openState.set(details.id, details.open));
      const groups = form.querySelector('.facets__groups');
      const scrollTop = groups ? groups.scrollTop : 0;
      const active = document.activeElement;
      const activeId = active && form.contains(active) ? active.id : '';

      form.innerHTML = fresh.innerHTML;

      form.querySelectorAll('details[id]').forEach((details) => {
        if (openState.has(details.id)) details.open = openState.get(details.id);
      });
      const newGroups = form.querySelector('.facets__groups');
      if (newGroups) newGroups.scrollTop = scrollTop;
      if (activeId) {
        const target = document.getElementById(activeId);
        if (target && form.contains(target)) target.focus({ preventScroll: true });
      }
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      const results = this.querySelector('[data-facets-results]');
      if (results) results.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    scrollToResults() {
      if (this.drawer && this.drawer.classList.contains('is-open')) return;
      const target = this.querySelector('[data-facets-active]') || this.querySelector('[data-facets-results]');
      if (!target) return;
      const offset = this.toolbar ? this.toolbar.getBoundingClientRect().bottom : 0;
      const top = target.getBoundingClientRect().top;
      if (top >= offset) return;
      window.scrollTo({
        top: Math.max(0, window.scrollY + top - offset),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    /* ---------- Drawer ---------- */

    openDrawer(trigger) {
      const drawer = this.drawer;
      if (!drawer) return;
      if (trigger) this.lastToggle = trigger;
      this.setDrawerInert(false);

      const glamora = window.Glamora;
      if (glamora && typeof glamora.openDrawer === 'function') {
        glamora.openDrawer(drawer);
      } else {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        const overlay = document.getElementById('DrawerOverlay');
        if (overlay) {
          overlay.classList.add('is-open');
          overlay.addEventListener('click', () => this.closeDrawer(), { once: true });
        }
        document.body.classList.add('is-locked');
        const first = drawer.querySelector('button, [href], input:not([disabled]), summary');
        if (first) first.focus();
      }
      this.syncDrawerState();
    }

    closeDrawer() {
      const drawer = this.drawer;
      if (!drawer || !drawer.classList.contains('is-open')) return;

      const glamora = window.Glamora;
      if (glamora && typeof glamora.closeDrawer === 'function') {
        glamora.closeDrawer(drawer);
      } else {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        const overlay = document.getElementById('DrawerOverlay');
        if (overlay) overlay.classList.remove('is-open');
        document.body.classList.remove('is-locked');
      }
      this.syncDrawerState();
    }

    syncDrawerState() {
      if (!this.drawer) return;
      const isOpen = this.drawer.classList.contains('is-open');
      this.querySelectorAll('[data-facets-open]').forEach((button) => {
        button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
      if (isOpen === this.drawerWasOpen) return;
      this.drawerWasOpen = isOpen;

      if (isOpen) {
        this.setDrawerInert(false);
        return;
      }
      const active = document.activeElement;
      const focusLost = !active || active === document.body || this.drawer.contains(active);
      if (focusLost && this.lastToggle && this.lastToggle.isConnected) {
        this.lastToggle.focus({ preventScroll: true });
      }
      this.setDrawerInert(true);
    }

    setDrawerInert(isInert) {
      if (!this.drawer) return;
      if (isInert) {
        this.drawer.setAttribute('inert', '');
      } else {
        this.drawer.removeAttribute('inert');
      }
    }

    /* ---------- Sticky toolbar offset (works with sticky, fixed or static headers) ---------- */

    findStickyHeaders() {
      const candidates = document.querySelectorAll(
        '.shopify-section-group-header-group, .shopify-section-group-header-group > *, header, [data-sticky-header]'
      );
      this.stickyHeaders = Array.from(candidates).filter((el) => {
        if (this.contains(el)) return false;
        // Drawers/dialogs (e.g. the closed mobile menu) are fixed but are not headers.
        if (el.closest('.drawer, [role="dialog"], [aria-modal="true"], .overlay')) return false;
        const rect = el.getBoundingClientRect();
        if (rect.height === 0 || rect.height > window.innerHeight * 0.5) return false;
        const position = window.getComputedStyle(el).position;
        return position === 'sticky' || position === 'fixed';
      });
    }

    updateStickyTop() {
      let offset = 0;
      this.stickyHeaders.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.height > 0 && rect.top <= 1 && rect.bottom > offset) offset = rect.bottom;
      });
      offset = Math.max(0, Math.round(offset));
      if (offset === this.stickyTop) return;
      this.stickyTop = offset;
      this.style.setProperty('--facets-sticky-top', `${offset}px`);
    }
  }

  customElements.define('facets-section', FacetsSection);
})();
